import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { QRCodeComponent } from 'angularx-qrcode';
import {
  ContainerComponent,
  RowComponent,
  ColComponent,
  CardComponent,
  CardHeaderComponent,
  CardBodyComponent,
  ButtonDirective,
  FormControlDirective,
  AlertComponent,
  InputGroupComponent,
  InputGroupTextDirective,
  SpinnerModule
} from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { UserPayoutService, UserPayoutInfo, PayoutToken } from 'app';
import { Web3Service, Web3ConnectionState } from 'credo';
import { I18nService, TranslatePipe } from 'i18n';
import { Logger } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-payout-request-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    FormControlDirective,
    AlertComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    QRCodeComponent,
    SpinnerModule,
    IconModule,
    TranslatePipe
  ],
  templateUrl: './user-payout-request-page.component.html',
  styleUrls: ['./user-payout-request-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPayoutRequestPageComponent implements OnDestroy {
  private logger = new Logger('UserPayoutRequestPageComponent');
  private userPayoutService = inject(UserPayoutService);
  private router = inject(Router);
  private web3Service = inject(Web3Service);
  private i18n = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  // ────────────────────────────────────────────────
  // State signals
  // ────────────────────────────────────────────────
  payoutInfo = signal<UserPayoutInfo | null>(null);
  loadingInfo = signal(true);

  newPayoutAmount = signal<number>(0);
  newPayoutWalletAddress = signal<string>('');
  newPayoutSubject = signal<string>('');

  // Settlement token (USDT/USDC). Defaults to USDC, which is always allowed.
  selectedToken = signal<PayoutToken>('USDC');
  // Explicit confirmation, required when choosing USDT, that the user is not a
  // European (EEA) resident.
  nonEuConfirmed = signal<boolean>(false);

  // Connected wallet's on-chain balance of the SELECTED token (USDT/USDC).
  walletTokenBalance = signal<string | null>(null);

  calculatedFee = signal<number>(0);
  calculatedAmountSent = signal<number>(0);

  creatingPayout = signal(false);
  confirmingPayout = signal(false);
  resendingCode = signal(false);
  error = signal<string | null>(null);
  success = signal<string | null>(null);

  awaitingConfirmation = signal(false);
  pendingPayoutId = signal<number | null>(null);
  confirmationCode = signal<string>('');
  confirmationExpiresAt = signal<Date | null>(null);

  // Web3 state (reactive)
  web3ConnectionState = signal<Web3ConnectionState>({
    connected: false,
    address: null,
    chainId: null,
    balance: null,
    walletConnectUri: null
  });

  connectingWallet = signal(false);
  isMobile = signal(this.detectMobile());

  // ────────────────────────────────────────────────
  // Computed
  // ────────────────────────────────────────────────
  // Tokens the user is allowed to receive. Europeans (or users without a
  // declared country) are restricted to USDC. Defaults to USDC-only until the
  // payout info has loaded (fail-safe).
  availableTokens = computed<PayoutToken[]>(() => this.payoutInfo()?.available_tokens ?? ['USDC']);
  isEuropean = computed(() => this.payoutInfo()?.european ?? true);
  canSelectUsdt = computed(() => this.availableTokens().includes('USDT'));
  usdtSelected = computed(() => this.selectedToken() === 'USDT');

  // USDT requires the "I am not a European resident" confirmation; USDC is
  // always permitted.
  isTokenSelectionValid = computed(() => {
    const token = this.selectedToken();
    if (token === 'USDT') {
      return this.canSelectUsdt() && this.nonEuConfirmed();
    }
    return this.availableTokens().includes(token);
  });

  canCreatePayout = computed(() => {
    const info = this.payoutInfo();
    if (!info) return false;

    const amount = this.newPayoutAmount();
    const address = this.newPayoutWalletAddress().trim();

    return !!(
      address &&
      this.isValidWalletAddress(address) &&
      amount >= (info.minimum_amount || 10) &&
      amount <= info.balance &&
      this.calculatedAmountSent() > 0 &&
      this.isTokenSelectionValid()
    );
  });

  isConfirmationExpired = computed(() => {
    const expires = this.confirmationExpiresAt();
    return expires ? Date.now() > expires.getTime() : false;
  });

  expirationTimeRemaining = computed(() => {
    const expires = this.confirmationExpiresAt();
    if (!expires) return '';

    const diff = expires.getTime() - Date.now();
    if (diff <= 0) return 'Expired';

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  private timerInterval: any;

  constructor() {
    // Load payout info once
    this.loadPayoutInfo();

    // React to Web3 connection state
    effect(() => {
      this.web3Service.connectionState$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(state => {
            this.web3ConnectionState.set({ ...state });
            if (state.connected && state.address) {
              this.newPayoutWalletAddress.set(state.address);
              this.refreshWalletBalance();
            } else {
              this.walletTokenBalance.set(null);
            }
          });
    });

    // Timer for confirmation expiration
    effect(() => {
      if (this.awaitingConfirmation() && this.confirmationExpiresAt()) {
        this.startConfirmationTimer();
      } else {
        this.stopConfirmationTimer();
      }
    });
  }

  private startConfirmationTimer(): void {
    this.stopConfirmationTimer();
    this.timerInterval = setInterval(() => {
      // Trigger recompute of computed signals
      this.confirmationExpiresAt.update(v => v);
    }, 1000);
  }

  private stopConfirmationTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  ngOnDestroy(): void {
    this.stopConfirmationTimer();
    if (this.web3ConnectionState().connected) {
      this.disconnectWallet();
    }
  }

  // ────────────────────────────────────────────────
  // Core actions
  // ────────────────────────────────────────────────
  private loadPayoutInfo(): void {
    this.loadingInfo.set(true);
    this.userPayoutService.getPayoutInfo()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: info => {
            this.payoutInfo.set(info);
            this.loadingInfo.set(false);
          },
          error: err => {
            this.logger.error('Failed to load payout info:', err);
            this.loadingInfo.set(false);
            this.error.set('Failed to load payout information');
          }
        });
  }

  onAmountChange(): void {
    const amount = this.newPayoutAmount();
    if (amount > 0) {
      const calc = this.userPayoutService.calculateFeeLocal(amount);
      this.calculatedFee.set(calc.fee);
      this.calculatedAmountSent.set(calc.amountSent);
    } else {
      this.calculatedFee.set(0);
      this.calculatedAmountSent.set(0);
    }
  }

  isValidWalletAddress(address: string): boolean {
    if (!address) return false;
    return /^0x[a-fA-F0-9]{40}$/.test(address.trim());
  }

  selectToken(token: PayoutToken): void {
    if (token === 'USDT' && !this.canSelectUsdt()) return;
    this.selectedToken.set(token);
    // Confirmation only applies to USDT; clear it whenever USDC is chosen.
    if (token !== 'USDT') {
      this.nonEuConfirmed.set(false);
    }
    // Show the wallet balance of the newly selected token.
    this.refreshWalletBalance();
  }

  // Fetch the connected wallet's balance for the currently selected token.
  private async refreshWalletBalance(): Promise<void> {
    const state = this.web3ConnectionState();
    if (!state.connected || !state.address) {
      this.walletTokenBalance.set(null);
      return;
    }
    try {
      const balance = await this.web3Service.getTokenBalance(state.address, this.selectedToken());
      this.walletTokenBalance.set(balance);
    } catch (err) {
      this.logger.error('Failed to fetch wallet token balance:', err);
      this.walletTokenBalance.set(null);
    }
  }

  submitPayout(): void {
    if (!this.canCreatePayout()) return;

    this.creatingPayout.set(true);
    this.error.set(null);

    this.userPayoutService.createPayout({
      amount: this.newPayoutAmount(),
      wallet_address: this.newPayoutWalletAddress().trim(),
      subject: this.newPayoutSubject() || undefined,
      token: this.selectedToken(),
      non_eu_confirmation: this.selectedToken() === 'USDT' ? this.nonEuConfirmed() : false
    })
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: response => {
        this.creatingPayout.set(false);
        this.awaitingConfirmation.set(true);
        this.pendingPayoutId.set(response.payout.id);
        this.confirmationExpiresAt.set(response.expires_at ? new Date(response.expires_at) : null);
        this.success.set(response.message);
        if (this.web3ConnectionState().connected) {
          this.disconnectWallet();
        }
      },
      error: err => {
        this.creatingPayout.set(false);
        this.error.set(err.error?.message || err.error?.error || 'Failed to create payout request');
      }
    });
  }

  confirmPayout(): void {
    const code = this.confirmationCode();
    const id = this.pendingPayoutId();

    if (!id || !code || code.length !== 6) {
      this.error.set('Please enter the 6-digit confirmation code');
      return;
    }

    if (this.isConfirmationExpired()) {
      this.error.set('Confirmation code expired');
      return;
    }

    if (this.confirmingPayout()) return;

    this.confirmingPayout.set(true);
    this.error.set(null);

    this.userPayoutService.confirmPayout(id, code)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.confirmingPayout.set(false);
            this.awaitingConfirmation.set(false);
            this.success.set(response.message || 'Payout request confirmed successfully!');
            setTimeout(() => this.navigateBack(), 2000);
          },
          error: err => {
            this.confirmingPayout.set(false);
            this.error.set(err.error?.message || err.error?.error || 'Invalid or expired confirmation code');
          }
        });
  }

  resendCode(): void {
    const id = this.pendingPayoutId();
    if (!id) return;

    this.resendingCode.set(true);
    this.error.set(null);

    this.userPayoutService.resendConfirmationCode(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: response => {
            this.resendingCode.set(false);
            this.confirmationExpiresAt.set(response.expires_at ? new Date(response.expires_at) : null);
            this.success.set(response.message || 'A new confirmation code has been sent to your email.');
            this.confirmationCode.set('');
          },
          error: err => {
            this.resendingCode.set(false);
            this.error.set(err.error?.message || err.error?.error || 'Failed to resend confirmation code');
          }
        });
  }

  cancelConfirmation(): void {
    const id = this.pendingPayoutId();
    if (!id) {
      this.resetForm();
      return;
    }

    this.userPayoutService.cancelPayout(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.resetForm(),
          error: () => this.resetForm() // reset locally anyway
        });
  }

  private resetForm(): void {
    this.awaitingConfirmation.set(false);
    this.pendingPayoutId.set(null);
    this.confirmationCode.set('');
    this.confirmationExpiresAt.set(null);
    this.error.set(null);
    this.success.set(null);
  }

  navigateBack(): void {
    const lang = this.i18n.getLanguage();
    this.router.navigate(['/', lang, 'my-payouts']);
  }

  formatAmount(amount?: number): string {
    return amount != null ? amount.toFixed(2) : '-';
  }

  // ────────────────────────────────────────────────
  // Web3 Wallet Methods
  // ────────────────────────────────────────────────
  private detectMobile(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(ua);
  }

  async connectWallet(forceQR = false): Promise<void> {
    this.connectingWallet.set(true);
    this.error.set(null);

    try {
      let state: Web3ConnectionState;
      if (forceQR) {
        state = await this.web3Service.connectWalletConnect();
      } else {
        state = await this.web3Service.connectWallet();
      }

      this.web3ConnectionState.set(state);
      if (state.connected && state.address) {
        this.newPayoutWalletAddress.set(state.address);
        this.refreshWalletBalance();
      }
    } catch (err: any) {
      this.error.set('Failed to connect wallet: ' + (err.message || 'Unknown error'));
    } finally {
      this.connectingWallet.set(false);
    }
  }

  hasBrowserWallet(): boolean {
    return this.web3Service.isWeb3Available();
  }

  async disconnectWallet(): Promise<void> {
    await this.web3Service.disconnectWallet();
    this.web3ConnectionState.set({
      connected: false,
      address: null,
      chainId: null,
      balance: null,
      walletConnectUri: null
    });
  }

  openMobileWallet(): void {
    const uri = this.web3ConnectionState().walletConnectUri;
    if (!uri) return;
    const deepLink = `wc:${uri.replace('wc:', '')}`;
    window.location.href = deepLink;
  }

  copyWalletConnectUri(): void {
    const uri = this.web3ConnectionState().walletConnectUri;
    if (!uri) return;

    navigator.clipboard.writeText(uri).then(() => {
      this.success.set('WalletConnect URI copied to clipboard');
      setTimeout(() => {
        if (this.success() === 'WalletConnect URI copied to clipboard') {
          this.success.set(null);
        }
      }, 3000);
    }).catch(err => {
      this.logger.error('Failed to copy URI:', err);
    });
  }

  shortenAddress(address: string | null): string {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  }

  // Synchronous check off the reactive connection state (BSC mainnet = 56).
  // Must NOT return a fresh Promise per change-detection: an async pipe over it
  // resets to null every cycle and the "switch network" warning sticks even on BSC.
  isCorrectNetwork(): boolean {
    return this.web3ConnectionState().chainId === 56;
  }

  getNetworkName(): string {
    const chainId = this.web3ConnectionState().chainId;
    return chainId === 56 ? 'BNB Smart Chain' : `Unknown (Chain ID: ${chainId ?? 'N/A'})`;
  }

  // The connected wallet's raw USDT balance comes back with full 18-decimal
  // precision; show it as a clean number.
  formatWalletBalance(balance: string | null): string {
    if (!balance) return '0.00';
    const n = parseFloat(balance);
    return isNaN(n) ? '0.00' : n.toFixed(2);
  }

  async switchNetwork(): Promise<void> {
    this.error.set(null);
    try {
      await this.web3Service.switchToBSC();
    } catch (err: any) {
      this.error.set('Failed to switch network: ' + (err.message || 'Please switch to BSC manually in your wallet'));
    }
  }
}